import { motion } from "framer-motion";
import DashboardMock from "@/components/site/DashboardMock";
import Button from "@/components/site/Button";

const sectors = [
  "Licensed clinics",
  "Registered pharmacies",
  "Cannabis based manufacturers",
  "Multi site operators",
  "Cultivation facilities",
];

export default function Hero() {
  return (
    <section
      className="relative pt-32 pb-14 md:pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      {/* backdrop */}
      <div className="absolute inset-0 grid-lines pointer-events-none opacity-70" />
      <div className="absolute -top-40 left-1/3 h-[480px] w-[720px] rounded-full bg-emerald-500/[0.09] blur-[130px] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-4 xl:gap-6 items-center">
          {/* Left column. copy */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-5 xl:col-span-6 relative z-10"
          >
            <div className="inline-flex items-center gap-2 rounded-sm border-l-2 border-[#D4A65A] pl-3 py-0.5 mb-8">
              <span className="eyebrow">
                Enterprise compliance assurance for UK medical cannabis
              </span>
            </div>

            <h1 className="font-display tracking-[-0.045em] text-white text-[52px] sm:text-6xl lg:text-[68px] xl:text-[72px] leading-[0.95] font-semibold">
              Inspection ready.
              <br />
              <span className="text-[#D4A65A] font-normal">
                Every day.
              </span>
            </h1>

            <p className="mt-8 text-slate-400 text-[17px] leading-[1.55] max-w-md">
              Distribute policies, verify comprehension and hold a
              permanent record ready when the inspector arrives.
            </p>

            {/* Interactive assessment invitation. A tile, not just a button. */}
            <motion.a
              href="#tryit"
              data-testid="hero-start-trial"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              whileHover={{ y: -2 }}
              className="group mt-9 relative block max-w-[440px] rounded-2xl border border-[#D4A65A]/40 hover:border-[#D4A65A]/70 bg-gradient-to-br from-[#0F1728] via-[#0B1424] to-[#080F1E] p-4 pr-5 shadow-[0_20px_60px_-20px_rgba(212,166,90,0.35),inset_0_1px_0_rgba(255,255,255,0.04)] transition-all overflow-hidden"
            >
              {/* ambient gold sheen */}
              <div className="pointer-events-none absolute -inset-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute -inset-y-2 -left-1/3 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-[#D4A65A]/10 to-transparent blur-xl" />
              </div>
              <div className="relative flex items-center gap-4">
                {/* Left seal */}
                <div className="relative shrink-0">
                  <div className="absolute inset-0 rounded-xl bg-[#D4A65A]/25 blur-lg" />
                  <div className="relative h-14 w-14 rounded-xl bg-gradient-to-br from-[#E6C078] to-[#B8862F] flex items-center justify-center ring-1 ring-[#F0D497]/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_8px_20px_-8px_rgba(212,166,90,0.6)]">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#0B1220"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 11 3 7v10l6-4M9 11l6 4M9 11V6l6-3v4M15 15l6 4V9l-6-3M15 15v4l-6-3v-5" />
                    </svg>
                  </div>
                  <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-[#0B1424] animate-pulse" />
                </div>

                {/* Middle content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[9.5px] uppercase tracking-[0.22em] text-[#D4A65A] font-semibold">
                      Interactive demo
                    </span>
                    <span className="text-[9.5px] uppercase tracking-[0.22em] text-slate-500">
                      · 90 seconds
                    </span>
                  </div>
                  <div className="mt-1 text-white text-[16px] font-semibold tracking-tight">
                    Try the assessment
                  </div>
                  <div className="mt-0.5 text-[12px] text-slate-400 leading-snug">
                    Answer three real questions. Earn a verifiable certificate.
                  </div>
                </div>

                {/* Right arrow */}
                <div className="shrink-0 h-9 w-9 rounded-full border border-white/10 group-hover:border-[#D4A65A]/60 flex items-center justify-center transition-colors">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#D4A65A"
                    strokeWidth="2.5"
                    className="transition-transform group-hover:translate-x-0.5"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Bottom stage flow */}
              <div className="relative mt-4 pt-3 border-t border-white/[0.06] flex items-center gap-2 text-[10px]">
                {[
                  ["Q.01", "#D4A65A"],
                  ["Q.02", "#D4A65A"],
                  ["Q.03", "#D4A65A"],
                  ["Cert", "#34D399"],
                ].map(([label, color], i, arr) => (
                  <span key={label} className="flex items-center gap-2">
                    <span
                      className="uppercase tracking-[0.18em] font-mono"
                      style={{ color }}
                    >
                      {label}
                    </span>
                    {i < arr.length - 1 && (
                      <span className="h-px w-4 bg-white/10" />
                    )}
                  </span>
                ))}
                <span className="ml-auto text-slate-500 tracking-[0.18em] uppercase text-[9.5px]">
                  Start now
                </span>
              </div>
            </motion.a>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button
                as="a"
                href="#contact"
                data-testid="hero-book-demo"
                variant="ghost"
                size="md"
              >
                Or book a personalised demo
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Button>
            </div>

            {/* Reassurance rail */}
            <div className="mt-14 pt-6 border-t border-white/[0.06] grid grid-cols-3 gap-4 max-w-md">
              {[
                ["GDPR", "aligned"],
                ["ISO 27001", "hosting"],
                ["CQC and MHRA", "ready"],
              ].map(([a, b]) => (
                <div key={a}>
                  <div className="text-white text-sm font-semibold tracking-tight">
                    {a}
                  </div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-[0.14em] mt-0.5">
                    {b}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right column. Floating product window, fully contained within viewport. */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
            className="lg:col-span-7 xl:col-span-6 relative"
          >
            <div className="relative mx-auto max-w-[520px] lg:max-w-[560px] xl:max-w-[620px] lg:pl-2 xl:pl-4">
              {/* Ambient glow, warm gold + faint green blend from brand artwork */}
              <div className="pointer-events-none absolute -inset-10 -z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4A65A]/[0.08] via-transparent to-emerald-500/[0.05] blur-3xl" />
              </div>
              {/* Depth plates behind the window */}
              <div className="pointer-events-none absolute -top-6 left-6 right-8 h-14 rounded-t-2xl border-t border-x border-white/[0.05] bg-white/[0.012] -z-10" />
              <div className="pointer-events-none absolute -top-3 left-3 right-4 h-10 rounded-t-2xl border-t border-x border-white/[0.07] bg-white/[0.02] -z-10" />

              {/* Floating window shell */}
              <div className="relative rounded-2xl shadow-[0_40px_100px_-30px_rgba(0,0,0,0.85),0_20px_45px_-20px_rgba(212,166,90,0.15)]">
                <DashboardMock />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Industry strip */}
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 mt-16">
        <div className="border-t border-white/[0.05] pt-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-4">
            Purpose built for
          </div>
          <ul
            data-testid="hero-sector-strip"
            className="flex flex-wrap items-center gap-x-6 gap-y-2.5 md:gap-x-8 lg:gap-x-10"
          >
            {sectors.map((l, i) => (
              <li
                key={l}
                className="flex items-center gap-x-6 md:gap-x-8 lg:gap-x-10"
              >
                <span className="font-display tracking-tight text-slate-300/85 text-[13px] md:text-sm whitespace-nowrap">
                  {l}
                </span>
                {i < sectors.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="hidden sm:inline-block h-1 w-1 rounded-full bg-slate-600/70"
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
